// DynamicPage.tsx (updated)
import React, { useEffect } from 'react';
import './dynamicPackage.less';
import { useLocation, useParams } from 'react-router-dom';
import SpinLoader from '@skygroup/shared/components/spin-loader/SpinLoader';
import NoResults from '../NoResults/NoResults';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '../../store/store';
import { useQuery } from '@tanstack/react-query';
import apiClient from '@skygroup/shared/API/apiClient';
import endpoints from '../../utils/endpoints';
import { fetchPageData } from './DynamicPackageApi';
import { message } from 'antd'; // <-- ant design message
import { setPageConfig } from '../../store/dynamicPageConfigSlice';
import LayoutWithMembershipGuard from '../Layout/LayoutWithMembershipGuard';

type DynamicPackageProps = {
  isPreviewMode: any;
  selectedInstance: any;
  setSelectedInstance: any;
  isEdit?: boolean;
  type?: any;
};

export default function DynamicPage({
  isPreviewMode,
  selectedInstance,
  setSelectedInstance,
  isEdit = false,
  type
}: DynamicPackageProps) {
  const dynamicPageConfig = useSelector((state: RootState) => state.dynamicPageConfig);
  const { secret } = useParams<{ secret: string }>();
  const location = useLocation();
  const dispatch = useDispatch()
  // promotion query (unchanged)
  const {
    isLoading: isPromotionLoading,
    data: promotionRes,
    error: promotionError,
    isError: isPromotionError,
  } = useQuery(
    ['page-conf-get', secret],
    async () => {
      const resp = await apiClient.get(`${endpoints.PROMOTION.GET}?secret=${secret}`);
      return resp?.data?.data;
    },
    {
      refetchOnWindowFocus: false,
      retry: false,
      enabled: !!secret,
    }
  );

  // page query (uses fetchPageData which will throw on API-level error)
  const {
    data: pageData,
    isLoading: isPageLoading,
    isError: isPageError,
    error: pageError,
  } = useQuery(
    ['dynamic-page-b2c', secret, type],
    async () => fetchPageData(secret as string, type),
    {
      enabled: !isEdit && !!secret,
      refetchOnWindowFocus: false,
      retry: false,
    }
  );
  // show AntD message when page fetch errors
  useEffect(() => {
    if (isPageError && pageError) {
      message.error((pageError as Error).message || 'Failed to load page');
    }
  }, [isPageError, pageError]);
  // update dynamicPageConfig in store when getting pageData because we need it inside of EditComponent
  useEffect(() => {
    if (pageData) {
      dispatch(setPageConfig({ dynamicPageConfig: pageData }))
    }
  }, [pageData]);
  // show AntD message if promotion query errors
  useEffect(() => {
    if (isPromotionError && promotionError) {
      message.error(
        (promotionError as any)?.message ||
        (promotionError as any)?.response?.data?.data?.message ||
        'Failed to load promotion'
      );
    }
  }, [isPromotionError, promotionError]);

  // EDIT MODE: render from store (unchanged but guarded)
  if (isEdit) {
    if (!dynamicPageConfig || !dynamicPageConfig.dynamicPageConfig) {
      return <NoResults noResults={true}>No dynamic page configuration found.</NoResults>;
    }
    const { pageConfig, modules } = dynamicPageConfig.dynamicPageConfig;

    return (
      <div>
        <LayoutWithMembershipGuard
          page={pageConfig}
          pageId={pageConfig?.name || pageConfig?.id}
          pagePath={location?.pathname}
          pageComponents={pageConfig?.modules}
          layout={pageConfig?.layout}
          isPreviewMode={isPreviewMode}
          selectedInstance={selectedInstance}
          setSelectedInstance={setSelectedInstance}
          dynamicModules={modules}
          promotionRes={promotionRes}
        />
      </div>
    );
  }

  // NON-EDIT MODE: show spinner while loading
  if (isPageLoading || isPromotionLoading) {
    return <SpinLoader isLoading={isPageLoading || isPromotionLoading} />;
  }

  // If query returned but payload is missing pageConfig — show friendly UI
  if (!pageData || !pageData.pageConfig) {
    // show NoResults and do not crash
    return <NoResults noResults={true}>Page not found.</NoResults>;
  }

  // Normal rendering
  return (
    <div>
      <NoResults noResults={false}>
        <LayoutWithMembershipGuard
          page={pageData.pageConfig}
          pageId={pageData.pageConfig.name || pageData.pageConfig?.id}
          pagePath={location?.pathname}
          pageComponents={pageData.pageConfig?.modules}
          layout={pageData.pageConfig?.layout}
          isPreviewMode={isPreviewMode}
          selectedInstance={selectedInstance}
          setSelectedInstance={setSelectedInstance}
          dynamicModules={pageData?.modules}
          promotionRes={promotionRes}
        />
      </NoResults>
    </div>
  );
}
